import React from "react";
import { Link } from "react-router-dom";
import instagramIcon from "../assets/footer/instagram.svg";
import snapchatIcon from "../assets/footer/snapchat.svg";
import tiktokIcon from "../assets/footer/tiktok.svg";
import whatsappIcon from "../assets/footer/whatsapp.svg";
import { useLanguage } from "../contexts/LanguageContext";

const Footer: React.FC = () => {

  const { t } = useLanguage();
  
  const openLink = (appUrl: string, webUrl: string) => {
    // Try opening the app URL
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

    if (isMobile) {
      const timeout = setTimeout(() => {
        window.location.href = webUrl; // fallback
      }, 800);

      // Try deep link
      window.location.href = appUrl;

      // If user leaves the page (app opened), cancel fallback
      window.addEventListener("blur", () => clearTimeout(timeout));
    } else {
      // Directly open web link for desktop
      window.open(webUrl, "_blank");
    }
  };
  return (
    <footer className="bg-[#ffda9c] text-neutral-800">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid md:grid-cols-3 gap-8">
          {/* Left Column - Connect with Mini Avonturiers */}
          <div className="space-y-6">
            <h3 className="text-xl font-child font-bold text-neutral-800 mb-2">
              {t("footer.connectWith")} 
            </h3>

            {/* Email Box */}
            <a href="mailto:info@mini-avonturiers.nl">
              <div className="border border-neutral-600 rounded-lg px-4 py-3 bg-[#FBEACD]/50 cursor-pointer hover:bg-[#FBEACD]/80 transition">
                <p className="text-neutral-800 text-center">
                  {t("footer.email")}
                </p>
              </div>
            </a>
            {/* Social Media Icons - Centered at bottom */}
            <div className="flex justify-center space-x-6 mt-8 pt-8 border-t border-neutral-600">
              {/* Instagram */}
              <button
                onClick={() =>
                  openLink(
                    "instagram://user?username=mini_avonturiers",
                    "https://www.instagram.com/mini_avonturiers/"
                  )
                }
                className="text-neutral-800 hover:text-secondary-orange transition-colors duration-200 p-2 rounded-lg hover:bg-[#FBEACD]/50"
              >
                <img src={instagramIcon} alt="Instagram" className="w-7 h-7" loading="lazy" />
              </button>

              {/* Snapchat */}
              <button
                onClick={() =>
                  openLink(
                    "snapchat://add/mini-avonturier",
                    "https://www.snapchat.com/add/mini-avonturier"
                  )
                }
                className="text-neutral-800 hover:text-secondary-orange transition-colors duration-200 p-2 rounded-lg hover:bg-[#FBEACD]/50"
              >
                <img src={snapchatIcon} alt="Snapchat" className="w-7 h-7" loading="lazy" />
              </button>

              {/* TikTok */}
              <button
                onClick={() =>
                  openLink(
                    "tiktok://user/@mini_avonturiers",
                    "https://www.tiktok.com/@mini_avonturiers"
                  )
                }
                className="text-neutral-800 hover:text-secondary-orange transition-colors duration-200 p-2 rounded-lg hover:bg-[#FBEACD]/50"
              >
                <img src={tiktokIcon} alt="TikTok" className="w-7 h-7" loading="lazy" />
              </button>

              {/* WhatsApp - Already works with web fallback */}
              <a
                href="https://wa.me/31616504171?text=Hello"
                target="_blank"
                rel="noopener noreferrer"
                className="text-neutral-800 hover:text-secondary-orange transition-colors duration-200 p-2 rounded-lg hover:bg-[#FBEACD]/50"
              >
                <img src={whatsappIcon} alt="WhatsApp" className="w-7 h-7" loading="lazy" />
              </a>
            </div>
          </div>

          {/* Middle Column - Navigation Links */}
          <div className="space-y-4">
            <div className="space-y-3">
              <Link
                to="/about"
                className="block text-neutral-800 hover:text-secondary-orange hover:bg-[#FBEACD]/50 transition-colors duration-200 py-2 px-3 rounded-lg"
              >
                {t("footer.aboutUs")}
              </Link>
              <Link
                to="/services"
                className="block text-neutral-800 hover:text-secondary-orange hover:bg-[#FBEACD]/50 transition-colors duration-200 py-2 px-3 rounded-lg"
              >
                {t("footer.services")}
              </Link>
              <Link
                to="/daily-routine"
                className="block text-neutral-800 hover:text-secondary-orange hover:bg-[#FBEACD]/50 transition-colors duration-200 py-2 px-3 rounded-lg"
              >
                {t("footer.dailyRoutine")}
              </Link>
              <Link
                to="/register"
                className="block text-neutral-800 hover:text-secondary-orange hover:bg-[#FBEACD]/50 transition-colors duration-200 py-2 px-3 rounded-lg"
              >
                {t("footer.registerNow")}
              </Link>
            </div>
          </div>

          {/* Right Column - Address and Telephone */}
          <div className="space-y-6">
            {/* Address Section */}
            <div className="space-y-3">
              <h3 className="text-xl font-child font-semibold text-neutral-800">
                {t("footer.address")}
              </h3>
              <div className="text-neutral-800">
                <p>{t("footer.addressText")}</p>
                <p>{t("footer.addressText2")}</p>
              </div>
            </div>

            {/* Telephone Section */}
            <div className="space-y-3">
              <h3 className="text-xl font-child font-semibold text-neutral-800">
                {t("footer.telephone")}
              </h3>
              <div className="text-neutral-800">
                <p>+31 6 16504171</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Copyright Section */}
      <div className="border-t border-neutral-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <p className="text-center text-sm text-neutral-800">
            {t("footer.copyright")}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
